import { Link } from 'react-router-dom';
import { ChevronRight, Clock, CalendarCheck } from 'lucide-react';
import { motion } from 'framer-motion';
import { MaskedLine, EASE } from '@/components/Reveal';
import { useLang, LangLink } from '@/i18n/LangContext';

export const Breadcrumbs = ({ items }) => (
  <nav aria-label="Breadcrumb" data-testid="breadcrumbs" className="flex items-center flex-wrap gap-1.5 text-xs text-muted-foreground mb-8">
    {items.map((item, i) => (
      <span key={i} className="flex items-center gap-1.5">
        {i > 0 && <ChevronRight className="h-3 w-3 opacity-50" />}
        {item.href && i < items.length - 1 ? (
          <LangLink to={item.href} className="hover:text-primary transition-colors duration-150">{item.label}</LangLink>
        ) : (
          <span className={i === items.length - 1 ? 'text-foreground/80 font-medium' : ''}>{item.label}</span>
        )}
      </span>
    ))}
  </nav>
);

export const PageHero = ({ eyebrow, title, summary, updated, readingTime, image, imageAlt, breadcrumbs }) => {
  const { t } = useLang();
  return (
  <section data-testid="page-hero" className="bg-background border-b border-border">
    <div className="mx-auto max-w-site px-5 md:px-8 pt-10 md:pt-16 pb-12 md:pb-16">
      {breadcrumbs && <Breadcrumbs items={breadcrumbs} />}
      {eyebrow && (
        <motion.p
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1, ease: EASE }}
          className="text-xs uppercase tracking-[0.25em] font-semibold text-primary mb-5"
        >
          {eyebrow}
        </motion.p>
      )}
      <h1 className="font-heading font-bold tracking-tighter leading-[1.02] text-4xl md:text-5xl lg:text-6xl max-w-4xl text-balance" data-testid="page-hero-title">
        <MaskedLine delay={0.2}>{title}</MaskedLine>
      </h1>
      {summary && (
        <motion.p
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.45, ease: EASE }}
          className="mt-6 text-base md:text-lg leading-relaxed text-foreground/70 max-w-2xl"
          data-testid="page-hero-summary"
        >
          {summary}
        </motion.p>
      )}
      {(updated || readingTime) && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="mt-7 flex flex-wrap items-center gap-5 text-xs text-muted-foreground"
          data-testid="page-hero-meta"
        >
          {updated && <span className="flex items-center gap-1.5"><CalendarCheck className="h-3.5 w-3.5" /> {t('metaReviewed')}: {updated}</span>}
          {readingTime && <span className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> {t('metaReading')}: {readingTime}</span>}
        </motion.div>
      )}
      {image && (
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.55, ease: EASE }}
          className="mt-10 overflow-hidden rounded-lg"
        >
          <img src={image} alt={imageAlt || title || ''} className="w-full h-56 md:h-80 object-cover" loading="lazy" decoding="async" />
        </motion.div>
      )}
    </div>
  </section>
  );
};
